import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import { adminAuditListQuery } from '@xdocs/schemas';
import type { AdminAuditListResult, AuditAction, AuditEvent, ErrorAlert } from '@xdocs/schemas';
import type { DataTableQuery } from '@xdocs/ui';
import { useWs } from '../../../ws/WsProvider.js';

const DEFAULT_QUERY: DataTableQuery = { page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' };
export const ADMIN_AUDIT_LIST_QUERY_KEY_PREFIX = 'admin-audit-list' as const;

export interface AuditFilters {
  actorUserId?: string | undefined;
  action?: AuditAction | undefined;
  targetType?: string | undefined;
  targetId?: string | undefined;
  impersonationOnly?: boolean | undefined;
  from?: Date | undefined;
  to?: Date | undefined;
}

export interface UseAuditListResult {
  rows: readonly AuditEvent[];
  total: number | null;
  nextCursor: string | null;
  query: DataTableQuery;
  onQueryChange: (next: DataTableQuery) => void;
  onLoadMore: () => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  isError: boolean;
  loadMoreError: string | undefined;
  onRetry: () => void;
}

/**
 * Server-side pagination/sort/filter over `admin.audit.list` (F59, E4 §8.3)
 * — the staff-scoped, UNFILTERED-by-actor read over `audit_events` (sibling
 * to `routes/... activity.list` the user-scoped WS-ACTIVITY task owns).
 * Read-only — this viewer is itself never an audited mutation (F59 AC).
 */
export function useAuditList(filters: AuditFilters = {}): UseAuditListResult {
  const { wsClient, connectionState } = useWs();
  const [query, setQuery] = useState<DataTableQuery>(DEFAULT_QUERY);
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const appendModeRef = useRef(false);
  const enabled = connectionState === 'connected';
  const queryKey = [
    ADMIN_AUDIT_LIST_QUERY_KEY_PREFIX,
    query,
    filters.actorUserId ?? null,
    filters.action ?? null,
    filters.targetType ?? null,
    filters.targetId ?? null,
    filters.impersonationOnly ?? false,
    filters.from?.toISOString() ?? null,
    filters.to?.toISOString() ?? null,
  ] as const;

  const result = useQuery<AdminAuditListResult, ErrorAlert>({
    queryKey,
    queryFn: () => requestTyped(wsClient, 'admin.audit.list', adminAuditListQuery.parse({ ...query, ...filters })),
    enabled,
  });

  useEffect(() => {
    if (!result.data) return;
    setRows((prev) => (appendModeRef.current ? [...prev, ...result.data.rows] : result.data.rows));
  }, [result.data]);

  // Filter changes reset to page 1 (fresh replace fetch, never append across
  // a filter boundary) — mirrors `useExplorerList`'s `folderId` reset. Plain
  // `.ts` file: `react-hooks/exhaustive-deps` isn't registered outside
  // `.jsx`/`.tsx` in this repo's ESLint config.
  useEffect(() => {
    appendModeRef.current = false;
    setQuery((prev) => ({ ...prev, page: 1 }));
  }, [
    filters.actorUserId,
    filters.action,
    filters.targetType,
    filters.targetId,
    filters.impersonationOnly,
    filters.from,
    filters.to,
  ]);

  const onQueryChange = useCallback((next: DataTableQuery) => {
    appendModeRef.current = false;
    setQuery(next);
  }, []);

  const onLoadMore = useCallback(() => {
    appendModeRef.current = true;
    setQuery((q) => ({ ...q, page: q.page + 1 }));
  }, []);

  const onRetry = useCallback(() => void result.refetch(), [result]);

  const total = result.data?.total ?? null;
  const hasMore = total !== null ? rows.length < total : false;
  const isAppendFetch = appendModeRef.current && result.isFetching;

  return {
    rows,
    total,
    nextCursor: hasMore ? 'more' : null,
    query,
    onQueryChange,
    onLoadMore,
    isLoading: result.isLoading && !appendModeRef.current,
    isLoadingMore: isAppendFetch,
    isError: result.isError,
    loadMoreError: appendModeRef.current && result.isError ? result.error?.message : undefined,
    onRetry,
  };
}
